/**
 * Centro de Notificações Premium — Sheet lateral enterprise.
 *
 * Header com identidade:
 *  - Eyebrow (ponto colorido + label uppercase)
 *  - Título serif + contador de não lidas
 *  - Busca premium com fundo + ícone + atalho ⌘K
 *  - Chips de módulo (scroll horizontal) + segmented status à direita
 *
 * @version 3.2.0 - 2026-06-05 — Correção do bug "lista some no mobile após Abrir negócio":
 *                                (a) showMobileDetail agora deriva de activeItem (não de activeId)
 *                                    para nunca esconder a lista quando o id ficar órfão;
 *                                (b) reset de activeId/seleção/busca ao fechar o Sheet;
 *                                (c) refetch defensivo ao reabrir o Sheet;
 *                                (d) não fecha mais o Sheet automaticamente no mobile ao abrir
 *                                    a entidade vinculada — o modal da entidade empilha em cima,
 *                                    evitando corrida de Dialogs do Radix.
 * @version 3.1.0 - 2026-06-05 — Botão de preferências agora abre o NotificationPreferencesSheet
 *                                unificado (matriz módulo×canal + DND + push). Popover legado removido.
 * @version 3.0.0 - 2026-04-20 — refinamento "Enterprise Premium"
 */
import { useEffect, useMemo, useState } from 'react';

import { Search, X, CheckSquare, Square, Settings2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import {
  useNotificationCenter,
  type NotificationCenterItem,
  type NotificationStatusFilter,
} from '@/hooks/useNotificationCenter';
import { NotificationModuleTabs } from './NotificationModuleTabs';
import { NotificationListPane } from './NotificationListPane';
import { NotificationDetailPane } from './NotificationDetailPane';
import { NotificationPreferencesSheet } from './NotificationPreferencesSheet';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Callback de "abrir entidade" (delegado para o Bell). */
  onOpenEntity: (n: NotificationCenterItem) => void;
}

const STATUS_OPTIONS: { id: NotificationStatusFilter; label: string }[] = [
  { id: 'unread',   label: 'Não lidas'  },
  { id: 'all',      label: 'Todas'      },
  { id: 'archived', label: 'Arquivadas' },
];

export function NotificationCenterSheet({ open, onOpenChange, onOpenEntity }: Props) {
  const isMobile = useIsMobile();
  const center = useNotificationCenter({ enabled: open });
  // Controla a abertura do Sheet unificado de Preferências (matriz + DND + push).
  const [prefsSheetOpen, setPrefsSheetOpen] = useState(false);

  // v3.2.0: usa activeItem (não activeId) para evitar que um id órfão — após
  // markRead/archive/refetch que removem o item do filtro atual — esconda a lista
  // no mobile e exiba o estado vazio do detalhe.
  const showMobileDetail = isMobile && !!center.activeItem;

  // v3.2.0: ao FECHAR o Sheet, limpa estado de sessão (id ativo, seleção, busca).
  // Garante começo limpo no próximo open mesmo se o Radix não desmontar o conteúdo
  // (cenário típico quando outro Dialog abre em cima e cria stacking).
  // Ao ABRIR, dispara refetch defensivo dos itens.
  useEffect(() => {
    if (!open) {
      center.setActiveId(null);
      center.clearSelection();
      center.setSearch('');
    } else {
      center.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Conta de não lidas exibida ao lado do título.
  const unreadCount = useMemo(
    () => center.items.filter((i) => !i.is_read && !i.archived_at).length,
    [center.items],
  );

  const handleSelectItem = (id: string) => {
    center.setActiveId(id);
    const item = center.items.find(i => i.id === id);
    if (item && !item.is_read) {
      center.markRead([id]);
    }
  };


  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex flex-col p-0 gap-0 overflow-hidden',
          'w-full sm:max-w-[680px] lg:!max-w-[980px]',
          'h-[100dvh] sm:h-full',
          'border-l border-border shadow-2xl',
          'sm:rounded-l-lg',
        )}
      >
        {/* Header enterprise */}
        <SheetHeader className="px-6 pt-5 pb-4 border-b border-border bg-card space-y-4 text-left">
          {/* Eyebrow + preferências */}
          <div className="flex items-center justify-between gap-2 pr-6">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
              <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Central de notificações
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Preferências de notificações"
              onClick={() => setPrefsSheetOpen(true)}
            >
              <Settings2 />
            </Button>
          </div>

          {/* Linha de título + busca alinhada à direita */}
          <div className="flex items-center gap-4">
            <div className="flex items-baseline gap-3 min-w-0">
              <SheetTitle
                className="text-[24px] font-medium tracking-tight leading-none text-foreground whitespace-nowrap"
              >
                Notificações
              </SheetTitle>
              {unreadCount > 0 && (
                <span className="text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">
                  <span className="text-muted-foreground/40 mr-1.5">·</span>
                  <span className="font-medium text-foreground">{unreadCount}</span> não {unreadCount === 1 ? 'lida' : 'lidas'}
                </span>
              )}
            </div>

            {/* Busca premium — agora inline com o título */}
            <div className="relative flex-1 max-w-[340px] ml-auto">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none"
                strokeWidth={1.75}
              />
              <input
                type="text"
                placeholder="Buscar notificações…"
                value={center.search}
                onChange={(e) => center.setSearch(e.target.value)}
                className={cn(
                  'w-full h-9 bg-muted/40 border border-border rounded-md',
                  'pl-9 pr-16 text-[13px] text-foreground',
                  'placeholder:text-muted-foreground/70',
                  'focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-foreground/30 focus:bg-background',
                  'transition-colors',
                )}
              />
              {center.search ? (
                <button
                  type="button"
                  onClick={() => center.setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label="Limpar busca"
                >
                  <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                </button>
              ) : (
                <kbd className="hidden sm:inline-flex absolute right-2.5 top-1/2 -translate-y-1/2 items-center gap-0.5 h-5 px-1.5 rounded border border-border bg-background text-[10px] font-medium text-muted-foreground tabular-nums select-none">
                  <span className="text-[11px] leading-none">⌘</span>
                  <span className="leading-none">K</span>
                </kbd>
              )}
            </div>
          </div>

          {/* Linha de filtros: chips de módulo (status movido para baixo, sobre a lista) */}
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <NotificationModuleTabs
                module={center.module}
                onChange={center.setModule}
                counts={center.countsByModule}
              />
            </div>
          </div>
        </SheetHeader>

        {/* Corpo — fundo neutro com cards flutuantes (estilo chat) */}
        <div className="relative flex-1 min-h-0 flex gap-3 bg-muted/30 p-3">
          {/* Card flutuante: Lista */}
          <div className={cn(
            'flex-1 min-w-0 flex flex-col rounded-xl border border-border bg-card shadow-sm overflow-hidden',
            isMobile && showMobileDetail && 'hidden',
            !isMobile && 'sm:max-w-[380px] lg:max-w-[420px]',
          )}>
            {/* Segmented status — ancorado acima da lista, à esquerda */}
            <div className="px-4 pt-3 pb-2 border-b border-border/60 flex items-center justify-between gap-3">
              <div className="inline-flex items-center text-[11.5px] tabular-nums">
                {STATUS_OPTIONS.map((opt, idx) => {
                  const active = center.status === opt.id;
                  const isFirst = idx === 0;
                  const isLast = idx === STATUS_OPTIONS.length - 1;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => center.setStatus(opt.id)}
                      className={cn(
                        'h-7 px-2.5 border transition-colors font-medium',
                        isFirst && 'rounded-l-md',
                        isLast && 'rounded-r-md',
                        idx > 0 && '-ml-px',
                        active
                          ? 'bg-primary text-primary-foreground border-primary z-10'
                          : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-accent/40',
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Ação rápida: Marcar todos / Limpar seleção */}
              {center.items.length > 0 && (() => {
                const allSelected =
                  center.selectedIds.size > 0 &&
                  center.selectedIds.size === center.items.length;
                return (
                  <button
                    type="button"
                    onClick={() =>
                      allSelected ? center.clearSelection() : center.selectAllVisible()
                    }
                    className={cn(
                      'inline-flex items-center gap-1.5 h-7 px-2 rounded-md',
                      'text-[11.5px] font-medium transition-colors',
                      'text-muted-foreground hover:text-foreground hover:bg-accent/40',
                    )}
                    aria-label={allSelected ? 'Limpar seleção' : 'Marcar todos'}
                  >
                    {allSelected ? (
                      <Square className="h-3.5 w-3.5" strokeWidth={1.75} />
                    ) : (
                      <CheckSquare className="h-3.5 w-3.5" strokeWidth={1.75} />
                    )}
                    {allSelected ? 'Limpar seleção' : 'Marcar todos'}
                  </button>
                );
              })()}
            </div>
            <div className="flex-1 min-h-0">
              <NotificationListPane
                items={center.items}
                loading={center.loading}
                activeId={center.activeId}
                selectedIds={center.selectedIds}
                onSelectItem={handleSelectItem}
                onToggleSelected={center.toggleSelected}
                onBulkMarkRead={() => center.markRead(
                  // Envia apenas os IDs ainda NÃO lidos — a RPC ignora os já lidos.
                  center.items.filter(i => center.selectedIds.has(i.id) && !i.is_read).map(i => i.id),
                )}
                onBulkMarkUnread={() => center.markUnread(
                  // Envia apenas os IDs LIDOS (a RPC filtra is_read = true internamente).
                  center.items.filter(i => center.selectedIds.has(i.id) && i.is_read).map(i => i.id),
                )}
                onBulkArchive={() => center.archive(Array.from(center.selectedIds))}
                onBulkClear={center.clearSelection}

              />
            </div>
          </div>


          {/* Card flutuante: Detalhe */}
          <div className={cn(
            'flex-1 min-w-0 flex flex-col rounded-xl border border-border bg-card shadow-sm overflow-hidden',
            isMobile && !showMobileDetail && 'hidden',
          )}>
            <NotificationDetailPane
              notification={center.activeItem}
              showBackButton={isMobile}
              onBack={() => center.setActiveId(null)}
              onOpenEntity={(n) => {
                // v3.2.0: NÃO fechamos mais o Sheet no mobile aqui. O modal da entidade
                // (Deal / Charge / Task / Booking) empilha em cima como Dialog do Radix.
                // Quando o usuário fecha esse modal, ele retorna naturalmente para o
                // detalhe da notificação — sem corrida de Dialogs e sem perder contexto.
                onOpenEntity(n);
              }}

              onMarkRead={(n) => center.markRead([n.id])}
              onMarkUnread={(n) => center.markUnread([n.id])}
              onArchive={(n) => center.archive([n.id])}

            />
          </div>
        </div>
      </SheetContent>

      {/* Sheet aninhado de Preferências — mesmo componente aberto pelo menu do avatar */}
      <NotificationPreferencesSheet
        open={prefsSheetOpen}
        onOpenChange={setPrefsSheetOpen}
      />
    </Sheet>
  );
}
